import React, { Dispatch, useEffect, useState } from 'react';
import { Typography } from '../../../primitives';
import enumToLabel from '../../../../utils/enumToLabel';
import { withStyles } from '@material-ui/core/styles';
import { Box, Grid, Divider, Hidden, Theme } from '@material-ui/core';
import { createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Transition } from '../../../confirmationDialog/ConfirmationDialog';
import TextField from '@material-ui/core/TextField';
import { IconButton } from '@material-ui/core';
import detailsPageStyles from '../../../commonStyles/detailsPage.style';
import configPageStyles from '../../../commonStyles/configPage.style';
import classNames from 'classnames';
import { INTERACTION } from '../../Configuration';
import { processSearchQuery } from '../searchQuery';
import Highlighter from 'react-highlight-words';
import { CurrentView, ModuleName } from '../Modules';

// Icons
import EditIcon from '../../../../utils/svgs/EditIcon';

// Redux
import { useSearchResultSections } from '../../useSearchResultSections';

interface Props {
  classes: { [key: string]: string };
  data: any;
  interaction?: INTERACTION;
  setPartialData: Dispatch<any>;
  currentView: CurrentView;
}

const initDialogData = {
  label: '',
  label_transition_in: '',
  key: null,
};

const StatesModule: React.FC<Props> = ({
  classes,
  data,
  interaction,
  setPartialData,
  currentView,
}) => {
  const [statesData, setStatesData] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogData, setDialogData] = useState<any>(initDialogData);

  const [searchResult, setSearchResult] = useState<string[]>([]);
  const [showWholeSection, setShowWholeSection] =
    useState<boolean>(true);

  const SECTION_TITLE = 'States';

  const { searchQuery, updateSections } = useSearchResultSections(
    ModuleName.GENERAL,
    SECTION_TITLE,
  );

  useEffect(
    function setStateData() {
      if (data && currentView !== CurrentView.SEARCH) {
        const tmpData: any[] = [];
        for (let item of Object.entries(data)) {
          const itemData = item[1] as {
            label: string;
            label_transition_in: string;
          };
          tmpData.push(itemData);
        }
        setStatesData(tmpData);
      }
    },
    [data, currentView],
  );

  useEffect(
    function filterStateDataBySearchResults() {
      if (currentView === CurrentView.SEARCH && data) {
        const tmpData: any[] = [];
        for (let item of Object.entries(data)) {
          const itemData = item[1] as {
            label: string;
            label_transition_in: string;
          };
          if (
            showWholeSection ||
            searchResult.some(
              (result) =>
                enumToLabel(item[0])
                  .toLowerCase()
                  .includes(result.toLowerCase()) ||
                itemData.label
                  .toLowerCase()
                  .includes(result.toLowerCase()) ||
                itemData.label_transition_in
                  .toLowerCase()
                  .includes(result.toLowerCase()),
            )
          )
            tmpData.push(itemData);
        }
        setStatesData(tmpData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, searchResult],
  );

  useEffect(
    function processSearch() {
      if (currentView === CurrentView.SEARCH) {
        //Show whole section is there are results in a title
        setShowWholeSection(
          processSearchQuery(searchQuery, [SECTION_TITLE]).length !==
            0,
        );

        const dynamicLabels: string[] = [];
        for (let item of Object.entries(data)) {
          const itemData = item[1] as {
            label: string;
            label_transition_in: string;
          };
          dynamicLabels.push(itemData.label);
          dynamicLabels.push(itemData.label_transition_in);
          dynamicLabels.push(enumToLabel(item[0]));
        }

        const results = processSearchQuery(searchQuery, [
          SECTION_TITLE,
          ...dynamicLabels,
        ]);
        setSearchResult(results);

        updateSections(results);
      } else {
        setSearchResult([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, currentView],
  );

  const getElKey = (obj: any) => {
    const el = Object.entries(data).find(
      ([key, value]: any) => value === obj,
    );
    return el?.length ? el[0] : '';
  };

  const onAfterDialogClose = () => {
    setShowDialog(false);
    setDialogData(initDialogData);
  };

  const renderDialog = () => {
    return (
      <Dialog
        classes={{ paperScrollPaper: classes.dialogRoot }}
        open={showDialog}
        TransitionComponent={Transition}
        onClose={() => {
          onAfterDialogClose();
        }}
      >
        <DialogTitle>
          <Typography>Edit</Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogRoot}>
          <TextField
            data-testid={`edit-label-${dialogData.label}`}
            autoFocus
            label={'Label'}
            InputLabelProps={{
              shrink: true,
              className: classes.inputNameLabel,
            }}
            InputProps={{
              disableUnderline: true,
              className: classes.inputName,
            }}
            classes={{
              root: classes.inputNameHolder,
            }}
            value={dialogData.label}
            onChange={(e) => {
              setDialogData({
                ...dialogData,
                label: e.target.value,
              });
            }}
          />
          <TextField
            data-testid={`edit-label-transitioning-in${dialogData.label_transition_in}`}
            label={'Label Transition In'}
            InputLabelProps={{
              shrink: true,
              className: classes.inputNameLabel,
            }}
            InputProps={{
              disableUnderline: true,
              className: classes.inputName,
            }}
            classes={{
              root: classes.inputNameHolder,
            }}
            value={dialogData.label_transition_in}
            onChange={(e) => {
              setDialogData({
                ...dialogData,
                label_transition_in: e.target.value,
              });
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              onAfterDialogClose();
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid={'edit-dialog-save-btn'}
            onClick={() => {
              data[dialogData.key].label = dialogData.label;
              data[dialogData.key].label_transition_in =
                dialogData.label_transition_in;
              setPartialData({
                ...data,
              });
              onAfterDialogClose();
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderComponent = () => {
    return (
      <>
        {renderDialog()}
        <Grid item sm={12}>
          <Box>
            <Box
              className={classNames(
                classes.moduleTitle,
                classes.pLeft,
              )}
              mb={1}
              mt={2}
            >
              <Highlighter
                highlightClassName={classes.searchResultsMark}
                searchWords={searchResult}
                autoEscape={true}
                textToHighlight={SECTION_TITLE}
              />
            </Box>
            <Box mt={2}>
              <Box mb={2}>
                <Grid
                  container
                  item
                  sm={12}
                  className={classes.textHeader}
                >
                  <Grid item sm={3} xs={10} className={classes.pLeft}>
                    Item
                  </Grid>
                  <Hidden xsDown>
                    <Grid item sm={3}>
                      Label
                    </Grid>
                    <Grid item sm={5}>
                      Label Transition In
                    </Grid>
                  </Hidden>
                  <Grid item sm={1} xs={2} />
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
              </Box>
              {statesData?.map(
                (partialDataEl: any, index: number, array: any[]) => {
                  return showWholeSection ||
                    searchResult.some(
                      (result) =>
                        enumToLabel(getElKey(partialDataEl))
                          .toLowerCase()
                          .includes(result.toLowerCase()) ||
                        partialDataEl.label
                          .toLowerCase()
                          .includes(result.toLowerCase()) ||
                        partialDataEl.label_transition_in
                          .toLowerCase()
                          .includes(result.toLowerCase()),
                    ) ? (
                    <Grid
                      container
                      item
                      sm={12}
                      key={`${partialDataEl.label}-${index}`}
                      className={classes.row}
                    >
                      <Grid
                        item
                        sm={3}
                        xs={10}
                        className={classNames(
                          classes.text,
                          classes.pLeft,
                        )}
                      >
                        <Highlighter
                          highlightClassName={
                            classes.searchResultsMark
                          }
                          searchWords={searchResult}
                          autoEscape={true}
                          textToHighlight={enumToLabel(
                            getElKey(partialDataEl),
                          )}
                        />
                      </Grid>
                      <Hidden xsDown>
                        <Grid item sm={3} className={classes.text}>
                          <Highlighter
                            highlightClassName={
                              classes.searchResultsMark
                            }
                            searchWords={searchResult}
                            autoEscape={true}
                            textToHighlight={partialDataEl.label}
                          />
                        </Grid>
                        <Grid item sm={5} className={classes.text}>
                          <Highlighter
                            highlightClassName={
                              classes.searchResultsMark
                            }
                            searchWords={searchResult}
                            autoEscape={true}
                            textToHighlight={
                              partialDataEl.label_transition_in
                            }
                          />
                        </Grid>
                      </Hidden>
                      <Grid item sm={1} xs={2}>
                        <IconButton
                          data-testid={`states-module-edit-btn-${enumToLabel(
                            getElKey(partialDataEl),
                          )}}`}
                          size='small'
                          className={classes.editIcon}
                          disabled={interaction === INTERACTION.READ}
                          onClick={() => {
                            setDialogData({
                              label: partialDataEl.label,
                              label_transition_in:
                                partialDataEl.label_transition_in,
                              key: getElKey(partialDataEl),
                            });
                            setShowDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Grid>
                      {index !== array.length - 1 && (
                        <Grid item xs={12}>
                          <Divider className={classes.divider} />
                        </Grid>
                      )}
                    </Grid>
                  ) : null;
                },
              )}
            </Box>
          </Box>
        </Grid>
      </>
    );
  };

  return currentView === CurrentView.SEARCH && !searchResult.length
    ? null
    : renderComponent();
};

export default withStyles(
  createStyles((theme: Theme) => ({
    ...detailsPageStyles,
    ...configPageStyles(theme),
  })),
)(StatesModule);
